import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SharedDisclosureProps } from '../shared/types';
import {
  CustomerUser,
  customerUsersApi,
} from '@texas/api/endpoints/customerUsersApi';
import { ReactSelectOption } from '@texas/types';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { TexasSelect } from '../shared/form/TexasSelect';
import { useEffect, useState } from 'react';

export interface SelectUserProps extends SharedDisclosureProps {
  customerId: number;
  onSelect: (user: CustomerUser) => void;
}
export function SelectCustomerUserModal({
  customerId,
  isOpen,
  onClose,
  onSelect,
}: SelectUserProps) {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState<CustomerUser | undefined>(
    undefined,
  );

  const {
    data: customerUsers,
    refetch: refetchCustomerUsers,
    loading: loadingCustomerUsers,
  } = useApiResource(customerUsersApi.getAvailableExternalUsers);

  useEffect(() => {
    refetchCustomerUsers(customerId);
  }, [customerId, refetchCustomerUsers]);

  const onConfirm = () => {
    onSelect(selectedUser!);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{t('customerUser.select')}</ModalHeader>
        <ModalBody>
          <Flex direction="column" gap={4} pb={4}>
            <Text variant="sub">{t('contacts.descriptionAvailableUsers')}</Text>
            <TexasSelect
              isLoading={loadingCustomerUsers}
              isClearable={true}
              value={getCustomerUser(customerUsers, selectedUser?.id)}
              onChange={(e) => setSelectedUser(e?.value)}
              placeholder={t('customerUser.select')}
              options={customerUsers?.map((u) => ({
                label: `${u.userName}${u.email ? ` (${u.email})` : ''}`,
                value: u,
              }))}
            />
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex gap={2}>
            <Button onClick={onClose}>{t('general.cancel')}</Button>
            <Button
              variant="texas-solid"
              disabled={!selectedUser}
              onClick={onConfirm}
            >
              {t('general.confirm')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function getCustomerUser(
  customerUsers: CustomerUser[] | null,
  userId: number | undefined,
): ReactSelectOption | undefined {
  if (customerUsers === null || userId === undefined) return undefined;

  const customerUser = customerUsers.find((u) => u.userId === userId);

  if (customerUser)
    return { label: customerUser.userName, value: customerUser.userId };
}
